import { useId, useState, type KeyboardEvent } from "react";

interface Props {
    label: string;
    value: string[];
    onChange: (next: string[]) => void;
    placeholder?: string;
    /** One-tap additions shown under the field until they are picked. */
    suggestions?: string[];
    max?: number;
    maxLength?: number;
    hint?: string;
}

/** A list of short words as pills: type and press Enter or comma to add, Backspace on an empty field removes the last. */
export function TagInput({ label, value, onChange, placeholder, suggestions = [], max = 20, maxLength = 60, hint }: Props) {
    const [draft, setDraft] = useState("");
    const id = useId();
    const full = value.length >= max;
    const has = (s: string) => value.some((v) => v.toLowerCase() === s.toLowerCase());

    const add = (raw: string) => {
        const next = raw.trim().slice(0, maxLength);
        if (next && !has(next) && !full) onChange([...value, next]);
        setDraft("");
    };
    const remove = (i: number) => onChange(value.filter((_, j) => j !== i));
    const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(draft); }
        else if (e.key === "Backspace" && draft === "" && value.length > 0) { e.preventDefault(); remove(value.length - 1); }
    };
    const open = suggestions.filter((s) => !has(s));

    return (
        <div className="field">
            <div className="field-head">
                <label htmlFor={id}>{label}</label>
                <span className="counter" aria-hidden>{value.length} / {max}</span>
            </div>
            <div className={`tags${full ? " full" : ""}`} onClick={(e) => (e.currentTarget.querySelector("input") as HTMLInputElement | null)?.focus()}>
                {value.map((t, i) => (
                    <span key={t} className="tag">
                        {t}
                        <button type="button" className="tag-x" aria-label={`Remove ${t}`} onClick={() => remove(i)}>×</button>
                    </span>
                ))}
                {!full && (
                    <input id={id} value={draft} maxLength={maxLength} placeholder={value.length === 0 ? placeholder : ""}
                        onChange={(e) => setDraft(e.target.value)} onKeyDown={onKey} onBlur={() => add(draft)}
                        aria-describedby={hint ? `${id}-hint` : undefined} />
                )}
            </div>
            {open.length > 0 && !full && (
                <div className="suggest" aria-label={`Suggested ${label.toLowerCase()}`}>
                    {open.map((s) => <button type="button" key={s} className="suggest-btn" onClick={() => add(s)}>+ {s}</button>)}
                </div>
            )}
            {hint && <p id={`${id}-hint`} className="hint-text">{hint}</p>}
        </div>
    );
}
